import { useEffect, useState, type FormEvent } from 'react';
import { GraduationCap, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Input } from '../ui/Input';
import { cn } from '../../lib/utils';

const WORKFLOW_TYPE_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'support_response', label: 'Support response' },
  { value: 'incident_triage', label: 'Incident triage' },
  { value: 'code_review', label: 'Code review' },
  { value: 'planning', label: 'Planning' },
];

export interface AiStreamOption {
  streamId: string;
  displayName: string;
}

export interface TeachDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  streams: AiStreamOption[];
  defaultStreamId: string;
  onSubmitted?: () => void;
}

export function TeachDialog({ open, onOpenChange, streams, defaultStreamId, onSubmitted }: TeachDialogProps) {
  const [streamId, setStreamId] = useState(defaultStreamId);
  const [workflowType, setWorkflowType] = useState('');
  const [content, setContent] = useState('');
  const [sourceLink, setSourceLink] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStreamId(defaultStreamId || streams[0]?.streamId || '');
      setFormError(null);
    }
  }, [open, defaultStreamId, streams]);

  const resetForm = () => {
    setContent('');
    setSourceLink('');
    setFile(null);
    setWorkflowType('');
    setFormError(null);
  };

  const handleClose = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const buildSource = (): string | undefined => {
    const parts: string[] = [];
    if (sourceLink.trim()) parts.push(sourceLink.trim());
    if (file) parts.push(`file:${file.name}`);
    return parts.length ? parts.join(' | ') : undefined;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const sid = streamId.trim();
    if (!sid) {
      setFormError('Select a stream.');
      return;
    }
    const bodyText = content.trim();
    if (!bodyText) {
      setFormError('Enter teaching content.');
      return;
    }
    if (file && file.size > 512_000) {
      setFormError('Attached file must be 512 KB or smaller.');
      return;
    }

    setSubmitting(true);
    try {
      let fullContent = bodyText;
      if (file && file.size > 0) {
        const text = await file.text();
        if (text.trim()) {
          fullContent = `${bodyText}\n\n---\n${file.name}\n${text}`;
        }
      }

      await apiClient.createAiWorkflowKnowledge(sid, {
        content: fullContent,
        type: workflowType || undefined,
        source: buildSource(),
      });
      resetForm();
      onOpenChange(false);
      onSubmitted?.();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const selectClass = cn(
    'w-full px-3 py-2 border rounded-md',
    'bg-white dark:bg-gray-800',
    'text-gray-900 dark:text-gray-100',
    'focus:outline-none focus:ring-2 focus:ring-blue-500',
    'border-gray-300 dark:border-gray-600',
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent maxWidth="2xl" className="flex flex-col min-h-0">
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Teach AI
              </span>
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            {formError && (
              <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
                {formError}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="teach-stream" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Stream
                </label>
                <select
                  id="teach-stream"
                  className={selectClass}
                  value={streamId}
                  onChange={(ev) => setStreamId(ev.target.value)}
                  disabled={streams.length === 0}
                >
                  {streams.length === 0 && <option value="">No streams</option>}
                  {streams.map((s) => (
                    <option key={s.streamId} value={s.streamId}>
                      {s.displayName || s.streamId}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="teach-workflow-type" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Workflow type
                </label>
                <select
                  id="teach-workflow-type"
                  className={selectClass}
                  value={workflowType}
                  onChange={(ev) => setWorkflowType(ev.target.value)}
                >
                  {WORKFLOW_TYPE_OPTIONS.map((o) => (
                    <option key={o.value || 'auto'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="teach-content" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Teaching content
              </label>
              <textarea
                id="teach-content"
                rows={6}
                value={content}
                onChange={(ev) => setContent(ev.target.value)}
                placeholder="Facts, preferences, or corrections the agent should remember…"
                className={cn(
                  'w-full px-3 py-2 border rounded-md resize-y min-h-[120px]',
                  'bg-white dark:bg-gray-800',
                  'text-gray-900 dark:text-gray-100',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500',
                  'border-gray-300 dark:border-gray-600',
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="teach-link" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Link (optional)
                </label>
                <Input
                  id="teach-link"
                  type="url"
                  placeholder="https://…"
                  value={sourceLink}
                  onChange={(ev) => setSourceLink(ev.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="teach-file" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  File (optional)
                </label>
                <Input
                  id="teach-file"
                  type="file"
                  onChange={(ev) => setFile(ev.target.files?.[0] ?? null)}
                  className="text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-800 dark:file:text-gray-200"
                />
                {file && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={file.name}>
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </p>
                )}
              </div>
            </div>
          </DialogBody>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || streams.length === 0}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting…
                </>
              ) : (
                'Submit teaching'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
